import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { agentsService } from '../services/agentsService';
import { voicesService } from '../services/voicesService';
import { knowledgebaseService } from '../services/knowledgebaseService';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import Dropzone from '../components/ui/dropzone';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { MoreHorizontal, Mic, Copy, Trash2, MessageCircle, Link, File, FileText, BookOpen, Plus, X, Search, AlertTriangle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Separator } from '../components/ui/separator';
import { Slider } from '../components/ui/slider';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';
import { ArrowLeft } from 'lucide-react';
import { StarBorder } from '../components/ui/star-border';
import { ToastSave } from '../components/ui/toast-save';

const LLM_MODELS = [
  // OpenAI models
  { label: 'GPT-4o Mini', value: 'gpt-4o-mini' },
  { label: 'GPT-4o', value: 'gpt-4o' },
  { label: 'GPT-4', value: 'gpt-4' },
  { label: 'GPT-4 Turbo', value: 'gpt-4-turbo' },
  { label: 'GPT-4.1', value: 'gpt-4.1' },
  { label: 'GPT-4.1 Mini', value: 'gpt-4.1-mini' },
  { label: 'GPT-4.1 Nano', value: 'gpt-4.1-nano' },
  { label: 'GPT-5', value: 'gpt-5' },
  { label: 'GPT-5 Mini', value: 'gpt-5-mini' },
  { label: 'GPT-5 Nano', value: 'gpt-5-nano' },
  { label: 'GPT-3.5 Turbo', value: 'gpt-3.5-turbo' },

  // Google models
  { label: 'Gemini 1.5 Pro', value: 'gemini-1.5-pro' },
  { label: 'Gemini 1.5 Flash', value: 'gemini-1.5-flash' },
  { label: 'Gemini 2.0 Flash', value: 'gemini-2.0-flash' },
  { label: 'Gemini 2.0 Flash Lite', value: 'gemini-2.0-flash-lite' },
  { label: 'Gemini 2.5 Flash Lite', value: 'gemini-2.5-flash-lite' },
  { label: 'Gemini 2.5 Flash', value: 'gemini-2.5-flash' },

  // Claude models
  { label: 'Claude Sonnet 4', value: 'claude-sonnet-4' },
  { label: 'Claude 3.7 Sonnet', value: 'claude-3-7-sonnet' },
  { label: 'Claude 3.5 Sonnet', value: 'claude-3-5-sonnet' },
  { label: 'Claude 3.5 Sonnet v1', value: 'claude-3-5-sonnet-v1' },
  { label: 'Claude 3 Haiku', value: 'claude-3-haiku' },

  // Other models
  { label: 'Grok Beta', value: 'grok-beta' },
  { label: 'Custom LLM', value: 'custom-llm' },
];

const LANGUAGES = [
  { label: 'English', value: 'en', flag: '🇺🇸' },
  { label: 'Spanish', value: 'es', flag: '🇪🇸' },
  { label: 'French', value: 'fr', flag: '🇫🇷' },
  { label: 'German', value: 'de', flag: '🇩🇪' },
  { label: 'Italian', value: 'it', flag: '🇮🇹' },
  { label: 'Portuguese', value: 'pt', flag: '🇵🇹' },
  { label: 'Chinese', value: 'zh', flag: '🇨🇳' },
  { label: 'Japanese', value: 'ja', flag: '🇯🇵' },
  { label: 'Korean', value: 'ko', flag: '🇰🇷' },
  { label: 'Arabic', value: 'ar', flag: '🇸🇦' },
  { label: 'Urdu', value: 'ur', flag: '🇵🇰' },
];

const TTS_OUTPUT_FORMATS = [
  { label: 'PCM 8000 Hz', value: 'pcm_8000' },
  { label: 'PCM 16000 Hz', value: 'pcm_16000' },
  { label: 'PCM 22050 Hz', value: 'pcm_22050' },
  { label: 'PCM 24000 Hz', value: 'pcm_24000' },
  { label: 'PCM 44100 Hz', value: 'pcm_44100' },
  { label: 'PCM 48000 Hz', value: 'pcm_48000' },
  { label: 'μ-law 8000 Hz', value: 'ulaw_8000' },
];

function AgentDetails() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [activeTab, setActiveTab] = useState('agent');
  const [toastSaveState, setToastSaveState] = useState('initial');

  // Controlled fields
  const [llm, setLlm] = useState('gpt-4o-mini');
  const [temperature, setTemperature] = useState(0.5);
  const [tokenLimit, setTokenLimit] = useState(-1);
  const [defaultLanguage, setDefaultLanguage] = useState('en');
  const [additionalLanguages, setAdditionalLanguages] = useState([]);
  const [firstMessage, setFirstMessage] = useState('Hey there, I\'m Adam from 44agents support. How can I help you today?');
  const [systemPrompt, setSystemPrompt] = useState('You\'re attentive and adaptive, matching the user\'s tone and mood—friendly, curious, respectful—without overstepping boundaries.');
  const [tools, setTools] = useState([
    { id: 'end_call', name: 'end_call', type: 'system', description: 'Gives agent the ability to end the call with the user.' }
  ]);
  // System tool toggles
  const [endCallEnabled, setEndCallEnabled] = useState(false);
  const [detectLanguageEnabled, setDetectLanguageEnabled] = useState(false);
  const [skipTurnEnabled, setSkipTurnEnabled] = useState(false);
  const [playKeypadTouchToneEnabled, setPlayKeypadTouchToneEnabled] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState({ id: 'cgSgspJ2msm6clMCkdW9', name: 'Jessica' });
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [authEnabled, setAuthEnabled] = useState(false);
  const [allowedDomains, setAllowedDomains] = useState(['*']);
  const [newDomain, setNewDomain] = useState('');
  const [enableOverrides, setEnableOverrides] = useState(false);
  const [ttsOutputFormat, setTtsOutputFormat] = useState('');
  // Add voice optimization states
  const [optimizeStreamingLatency, setOptimizeStreamingLatency] = useState(0);
  const [stability, setStability] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [similarityBoost, setSimilarityBoost] = useState(0);
  // Add analysis states
  const [evaluationCriteria, setEvaluationCriteria] = useState([]);
  const [dataCollection, setDataCollection] = useState({});
  const [originalEvaluationCriteria, setOriginalEvaluationCriteria] = useState([]);
  const [originalDataCollection, setOriginalDataCollection] = useState({});

  // Add modal states
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);
  const [editingCriteria, setEditingCriteria] = useState(null);
  const [criteriaName, setCriteriaName] = useState("");
  const [criteriaPrompt, setCriteriaPrompt] = useState("");
  const [useKnowledgeBase, setUseKnowledgeBase] = useState(false);

  // Add data collection modal states
  const [showDataCollectionModal, setShowDataCollectionModal] = useState(false);
  const [editingDataItem, setEditingDataItem] = useState(null);
  const [dataItemIdentifier, setDataItemIdentifier] = useState("");
  const [dataItemType, setDataItemType] = useState("string");
  const [dataItemDescription, setDataItemDescription] = useState("");
  const [availableTypes] = useState(["String", "Boolean", "Integer", "Number"]);
  
  const modalRef = useRef(null);
  const dataCollectionModalRef = useRef(null);

  // Add state for call limits and overrides
  const [agentConcurrencyLimit, setAgentConcurrencyLimit] = useState(-1);
  const [dailyCallsLimit, setDailyCallsLimit] = useState(100000);
  const [overrideAgentLanguage, setOverrideAgentLanguage] = useState(false);
  const [overrideSystemPrompt, setOverrideSystemPrompt] = useState(false);
  const [overrideFirstMessage, setOverrideFirstMessage] = useState(false);
  const [overrideVoice, setOverrideVoice] = useState(false);
  // Add state for advanced call limits
  const [turnTimeout, setTurnTimeout] = useState(7);
  const [silenceEndCallTimeout, setSilenceEndCallTimeout] = useState(-1);
  const [maxConversationDuration, setMaxConversationDuration] = useState(300);

  // Knowledge base states
  const [knowledgeBaseDocuments, setKnowledgeBaseDocuments] = useState([]);
  const [isLoadingKnowledgeBase] = useState(false);
  const [allDocuments, setAllDocuments] = useState([]);
  const [addDocumentOpen, setAddDocumentOpen] = useState(false);
  const [documentSearchTerm, setDocumentSearchTerm] = useState('');
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [typeSelectionOpen, setTypeSelectionOpen] = useState(false);

  // Dialog states
  const [addUrlDialogOpen, setAddUrlDialogOpen] = useState(false);
  const [addTextDialogOpen, setAddTextDialogOpen] = useState(false);
  const [addFilesDialogOpen, setAddFilesDialogOpen] = useState(false);
  
  // Form states
  const [documentName, setDocumentName] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');
  const [documentText, setDocumentText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Fetch voices from API
  useEffect(() => {
    async function fetchVoices() {
      setLoadingVoices(true);
      try {
        // Modified to fetch with page_size=50
        const response = await voicesService.getVoices({ page_size: 55 });
        
        if (Array.isArray(response) && response.length > 0) {
          setVoices(response);
        } else {
          setVoices([]);
        }
      } catch (error) {
        setVoices([]);
      } finally {
        setLoadingVoices(false);
      }
    }

    fetchVoices();
  }, []);

  // Normalize domain data to ensure it's always an array of strings
  const normalizeDomains = (domains) => {
    if (!domains) return ['*'];
    if (!Array.isArray(domains)) return ['*'];
    if (domains.length === 0) return ['*'];
    
    // Convert any non-string items to strings
    return domains.map(domain => {
      if (typeof domain === 'string') return domain;
      if (domain && typeof domain === 'object' && domain.hostname) return domain.hostname;
      return JSON.stringify(domain);
    });
  };

  // Normalize language presets data to extract additional languages
  const extractAdditionalLanguages = (languagePresets, defaultLanguage) => {
    if (!languagePresets || typeof languagePresets !== 'object') return [];
    
    // If it's an object with language keys, extract the keys
    return Object.keys(languagePresets).filter(lang => 
      lang && lang !== defaultLanguage
    );
  };

  // Normalize TTS output format to ensure proper format
  const normalizeTTSFormat = (format) => {
    if (!format) return '';
    // Ensure lowercase and remove any extra spaces
    return format.toLowerCase().trim();
  };
  
  // Get TTS format details from value
  const getTTSFormatDetails = useCallback((value) => {
    const normalizedValue = normalizeTTSFormat(value);
    return TTS_OUTPUT_FORMATS.find(format => format.value === normalizedValue) || null;
  }, []);

  useEffect(() => {
    async function fetchDetails() {
      setIsLoading(true);
      try {
        const data = await agentsService.getAgentById(agentId);
        setAgent(data);
        
        // Set LLM model & temperature
        setLlm(data.conversation_config?.agent?.prompt?.llm || 'gpt-4o-mini');
        setTemperature(data.conversation_config?.agent?.prompt?.temperature ?? 0.5);
        setTokenLimit(data.conversation_config?.agent?.prompt?.max_tokens ?? -1);
        
        // Set default language
        setDefaultLanguage(data.conversation_config?.agent?.language || 'en');
        
        // Get additional languages from conversation_config.language_presets
        if (data.conversation_config?.language_presets) {
          setAdditionalLanguages(extractAdditionalLanguages(
            data.conversation_config.language_presets,
            data.conversation_config?.agent?.language || 'en'
          ));
        } else {
          setAdditionalLanguages([]);
        }
        
        // Set first message
        setFirstMessage(data.conversation_config?.agent?.first_message || 'Hey there, I\'m Alexis from 44agents support. How can I help you today?');
        
        // Set system prompt
        setSystemPrompt(data.conversation_config?.agent?.prompt?.prompt || 'You\'re attentive and adaptive');
        
        // Set tools and system tool toggles
        if (data.conversation_config?.agent?.prompt?.tools) {
          const allTools = data.conversation_config.agent.prompt.tools;
          
          // Separate system tools from custom tools
          const systemTools = allTools.filter(tool => tool.type === 'system');
          const customTools = allTools.filter(tool => tool.type !== 'system');
          
          // Set custom tools
          setTools(customTools);
          
          // Set system tool toggles based on what's in the tools array
          setEndCallEnabled(systemTools.some(tool => tool.name === 'end_call'));
          setDetectLanguageEnabled(systemTools.some(tool => tool.name === 'language_detection'));
          setSkipTurnEnabled(systemTools.some(tool => tool.name === 'skip_turn'));
          setPlayKeypadTouchToneEnabled(systemTools.some(tool => tool.name === 'play_keypad_touch_tone'));
        } else {
          setTools([]);
          // Default system tool states
          setEndCallEnabled(false);
          setDetectLanguageEnabled(false);
          setSkipTurnEnabled(false);
          setPlayKeypadTouchToneEnabled(false);
        }
        
        // Set TTS output format
        const audioFormat = data.conversation_config?.tts?.agent_output_audio_format;
        const normalizedFormat = normalizeTTSFormat(audioFormat);
        setTtsOutputFormat(normalizedFormat);
        
        // Set voice optimization settings
        setOptimizeStreamingLatency(data.conversation_config?.tts?.optimize_streaming_latency ?? 0);
        setStability(data.conversation_config?.tts?.stability ?? 0);
        setSpeed(data.conversation_config?.tts?.speed ?? 0);
        setSimilarityBoost(data.conversation_config?.tts?.similarity_boost ?? 0);
        
        // Set evaluation criteria
        if (data.platform_settings?.evaluation?.criteria && Array.isArray(data.platform_settings.evaluation.criteria)) {
          setEvaluationCriteria(data.platform_settings.evaluation.criteria);
          setOriginalEvaluationCriteria(data.platform_settings.evaluation.criteria);
        } else {
          setEvaluationCriteria([]);
          setOriginalEvaluationCriteria([]);
        }
        
        // Set data collection - handle as object with dynamic keys
        if (data.platform_settings?.data_collection && typeof data.platform_settings.data_collection === 'object') {
          setDataCollection(data.platform_settings.data_collection);
          setOriginalDataCollection(data.platform_settings.data_collection);
        } else {
          setDataCollection({});
          setOriginalDataCollection({});
        }
        
        // Set voice data if available
        if (data.conversation_config?.tts?.voice_id) {
          const voiceId = data.conversation_config.tts.voice_id;
          
          // Try to find the voice in the fetched list
          const voiceFromList = voices.find(v => v.voice_id === voiceId);
          
          if (voiceFromList) {
            setSelectedVoice({ id: voiceFromList.voice_id, name: voiceFromList.name });
          } else if (voices.length > 0) {
            // If voice not in list and voices are loaded, fetch it individually from the API
            try {
              const voiceDetails = await voicesService.getVoiceById(voiceId);
              if (voiceDetails && (voiceDetails.voice_id || voiceDetails.id)) {
                setSelectedVoice({ 
                  id: voiceDetails.voice_id || voiceDetails.id, 
                  name: voiceDetails.name 
                });
              } else {
                setSelectedVoice({ id: voiceId, name: `Voice ${voiceId.substring(0, 6)}...` });
              }
            } catch (error) {
              setSelectedVoice({ id: voiceId, name: `Voice ${voiceId.substring(0, 6)}...` });
            }
          } else {
            // If voices haven't loaded yet, set the voice ID and wait for voices to load
            setSelectedVoice({ id: voiceId, name: `Voice ${voiceId.substring(0, 6)}...` });
          }
        } else {
          // If no voice_id in config, use default
          setSelectedVoice({ id: 'cgSgspJ2msm6clMCkdW9', name: 'Jessica' });
        }
        
        // Set model type (flash vs turbo) - removed unused variables

        // Set authentication settings
        setAuthEnabled(!!data.platform_settings?.auth?.enable_auth);
        
        // Set enable overrides
        setEnableOverrides(!!data.platform_settings?.overrides?.conversation_config_override);
        
        // Get allowlist from platform_settings.auth.allowlist
        if (data.platform_settings?.auth?.allowlist) {
          setAllowedDomains(normalizeDomains(data.platform_settings.auth.allowlist));
        } else {
          setAllowedDomains(['*']);
        }

        // Add state for call limits and overrides
        setAgentConcurrencyLimit(data.platform_settings?.call_limits?.agent_concurrency_limit ?? -1);
        setDailyCallsLimit(data.platform_settings?.call_limits?.daily_limit ?? 100000);
        const overrides = data.platform_settings?.overrides?.conversation_config_override || {};
        setOverrideAgentLanguage(!!overrides.agent?.language);
        setOverrideSystemPrompt(!!overrides.agent?.prompt?.prompt);
        setOverrideFirstMessage(!!overrides.agent?.first_message);
        setOverrideVoice(!!overrides.tts?.voice_id);
        // Add state for advanced call limits
        setTurnTimeout(data.conversation_config?.turn?.turn_timeout ?? 7);
        setSilenceEndCallTimeout(data.conversation_config?.turn?.silence_end_call_timeout ?? -1);
        setMaxConversationDuration(data.conversation_config?.conversation?.max_duration_seconds ?? 300);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    }
    fetchDetails();
  }, [agentId, voices, getTTSFormatDetails]);

  // Set initial load complete when agent data is loaded
  useEffect(() => {
    if (!isLoading && agent) {
      const timer = setTimeout(() => {
        setInitialLoadComplete(true);
      }, 1000); // Give time for all state to be set
      return () => clearTimeout(timer);
    }
  }, [isLoading, agent]);

  // Listen for changes to set the unsaved changes flag
  useEffect(() => {
    // Only run the check if loading is complete, we have agent data, and initial load is complete
    // This prevents the unsaved changes flag from appearing during initial load
    if (!isLoading && agent && initialLoadComplete) {
      const timeoutId = setTimeout(() => {
      // Compare exact string representations for object equality
      const criteriaChanged = JSON.stringify(evaluationCriteria) !== JSON.stringify(originalEvaluationCriteria);
      const dataCollectionChanged = JSON.stringify(dataCollection) !== JSON.stringify(originalDataCollection);
      
      
      const originalConcurrency = agent.platform_settings?.call_limits?.agent_concurrency_limit ?? -1;
      const originalDailyLimit = agent.platform_settings?.call_limits?.daily_limit ?? 100000;
      const originalOverrides = agent.platform_settings?.overrides?.conversation_config_override || {};
      const overridesChanged =
        overrideAgentLanguage !== !!originalOverrides?.agent?.language ||
        overrideSystemPrompt !== !!originalOverrides?.agent?.prompt?.prompt ||
        overrideFirstMessage !== !!originalOverrides?.agent?.first_message ||
        overrideVoice !== !!originalOverrides?.tts?.voice_id;
      const callLimitsChanged =
        agentConcurrencyLimit !== originalConcurrency ||
        dailyCallsLimit !== originalDailyLimit;
      const originalTurnTimeout = agent.conversation_config?.turn?.turn_timeout ?? 7;
      const originalSilenceTimeout = agent.conversation_config?.turn?.silence_end_call_timeout ?? -1;
      const originalMaxDuration = agent.conversation_config?.conversation?.max_duration_seconds ?? 300;
      const advancedCallLimitsChanged =
        turnTimeout !== originalTurnTimeout ||
        silenceEndCallTimeout !== originalSilenceTimeout ||
        maxConversationDuration !== originalMaxDuration;
      
      // Check each field individually for debugging
      const llmChanged = llm !== (agent.conversation_config?.agent?.prompt?.llm || 'gpt-4o-mini');
      const temperatureChanged = temperature !== (agent.conversation_config?.agent?.prompt?.temperature ?? 0.5);
      const tokenLimitChanged = tokenLimit !== (agent.conversation_config?.agent?.prompt?.max_tokens ?? -1);
      const defaultLanguageChanged = defaultLanguage !== (agent.conversation_config?.agent?.language || 'en');
      const additionalLanguagesChanged = JSON.stringify(additionalLanguages.sort()) !== 
          JSON.stringify(extractAdditionalLanguages(
            agent.conversation_config?.language_presets,
            agent.conversation_config?.agent?.language || 'en'
          ).sort());
      const firstMessageChanged = firstMessage !== (agent.conversation_config?.agent?.first_message || 'Hey there, I\'m Alexis from 44agents support. How can I help you today?');
      const systemPromptChanged = systemPrompt !== (agent.conversation_config?.agent?.prompt?.prompt || 'You\'re attentive and adaptive');
      // Check if system tools have changed
      const originalTools = agent.conversation_config?.agent?.prompt?.tools || [];
      const originalSystemTools = originalTools.filter(tool => tool.type === 'system');
      const originalCustomTools = originalTools.filter(tool => tool.type !== 'system');
      
      const originalEndCallEnabled = originalSystemTools.some(tool => tool.name === 'end_call');
      const originalDetectLanguageEnabled = originalSystemTools.some(tool => tool.name === 'language_detection');
      const originalSkipTurnEnabled = originalSystemTools.some(tool => tool.name === 'skip_turn');
      const originalPlayKeypadTouchToneEnabled = originalSystemTools.some(tool => tool.name === 'play_keypad_touch_tone');
      
      const systemToolsChanged = 
        endCallEnabled !== originalEndCallEnabled ||
        detectLanguageEnabled !== originalDetectLanguageEnabled ||
        skipTurnEnabled !== originalSkipTurnEnabled ||
        playKeypadTouchToneEnabled !== originalPlayKeypadTouchToneEnabled;
      
      const customToolsChanged = JSON.stringify(tools) !== JSON.stringify(originalCustomTools);
      const toolsChanged = systemToolsChanged || customToolsChanged;
      const voiceChanged = selectedVoice.id !== (agent.conversation_config?.tts?.voice_id || 'cgSgspJ2msm6clMCkdW9');
      const ttsFormatChanged = normalizeTTSFormat(ttsOutputFormat) !== normalizeTTSFormat(agent.conversation_config?.tts?.agent_output_audio_format);
      const optimizeLatencyChanged = optimizeStreamingLatency !== (agent.conversation_config?.tts?.optimize_streaming_latency ?? 0);
      const stabilityChanged = stability !== (agent.conversation_config?.tts?.stability ?? 0);
      const speedChanged = speed !== (agent.conversation_config?.tts?.speed ?? 0);
      const similarityBoostChanged = similarityBoost !== (agent.conversation_config?.tts?.similarity_boost ?? 0);
      const authEnabledChanged = authEnabled !== (agent.platform_settings?.auth?.enable_auth || false);
      const allowedDomainsChanged = JSON.stringify(allowedDomains) !== JSON.stringify(normalizeDomains(agent.platform_settings?.auth?.allowlist));
      const enableOverridesChanged = enableOverrides !== !!agent.platform_settings?.overrides?.conversation_config_override;

      if (enableOverridesChanged) {
      }

      // Check if knowledge base documents have changed
      const originalKnowledgeBase = agent.conversation_config?.agent?.prompt?.knowledge_base || [];
      const knowledgeBaseChanged = JSON.stringify(knowledgeBaseDocuments) !== JSON.stringify(originalKnowledgeBase);

      const hasChanges = 
        llmChanged || temperatureChanged || tokenLimitChanged ||
        defaultLanguageChanged || additionalLanguagesChanged || firstMessageChanged ||
        systemPromptChanged || toolsChanged || voiceChanged || ttsFormatChanged ||
        optimizeLatencyChanged || stabilityChanged || speedChanged || similarityBoostChanged ||
        authEnabledChanged || allowedDomainsChanged || enableOverridesChanged ||
        criteriaChanged || dataCollectionChanged || overridesChanged ||
        callLimitsChanged || advancedCallLimitsChanged || knowledgeBaseChanged;

      setHasUnsavedChanges(hasChanges);
      }, 500); // Increased delay to ensure all state is properly initialized

      return () => clearTimeout(timeoutId);
    } else {
      // Set to false while loading
      setHasUnsavedChanges(false);
    }
  }, [
    llm, temperature, tokenLimit, defaultLanguage, additionalLanguages,
    firstMessage, systemPrompt, tools, agent, isLoading, selectedVoice, ttsOutputFormat,
    authEnabled, allowedDomains, enableOverrides, optimizeStreamingLatency,
    stability, speed, similarityBoost, evaluationCriteria, dataCollection,
    originalEvaluationCriteria, originalDataCollection,
    agentConcurrencyLimit, dailyCallsLimit, overrideAgentLanguage, overrideSystemPrompt, overrideFirstMessage, overrideVoice,
    turnTimeout, silenceEndCallTimeout, maxConversationDuration, initialLoadComplete,
    endCallEnabled, detectLanguageEnabled, skipTurnEnabled, playKeypadTouchToneEnabled,
    knowledgeBaseDocuments
  ]);

  // Add handlers for deleting criteria and data collection items
  const handleDeleteCriteria = (criteriaId) => {
    const updatedCriteria = evaluationCriteria.filter(criteria => criteria.id !== criteriaId);
    setEvaluationCriteria(updatedCriteria);
    setHasUnsavedChanges(true);
  };

  const handleDeleteDataCollectionItem = (itemKey) => {
    const updatedDataCollection = { ...dataCollection };
    delete updatedDataCollection[itemKey];
    setDataCollection(updatedDataCollection);
    setHasUnsavedChanges(true);
  };

  // Add handlers for opening the add/edit modal


  const openEditCriteriaModal = (criteria) => {
    document.body.style.overflow = 'hidden'; // Prevent scrolling
    setEditingCriteria(criteria);
    setCriteriaName(criteria.id);
    setCriteriaPrompt(criteria.conversation_goal_prompt);
    setUseKnowledgeBase(criteria.use_knowledge_base || false);
    setShowCriteriaModal(true);
  };

  const closeCriteriaModal = () => {
    document.body.style.overflow = ''; // Restore scrolling
    setShowCriteriaModal(false);
  };

  // Fetch all documents function
  const fetchAllDocuments = async () => {
    try {
      const response = await knowledgebaseService.getDocuments();
      setAllDocuments(response.documents || []);
    } catch (err) {
      console.error('Failed to fetch all documents:', err);
      toast.error('Failed to load documents');
    }
  };

  // Fetch all documents when component mounts
  useEffect(() => {
    fetchAllDocuments();
  }, []);

  // Set knowledge base documents from agent's conversation_config when agent is loaded
  useEffect(() => {
    if (agent && agent.conversation_config?.agent?.prompt?.knowledge_base) {
      setKnowledgeBaseDocuments(agent.conversation_config.agent.prompt.knowledge_base);
    } else if (agent) {
      setKnowledgeBaseDocuments([]);
    }
  }, [agent]);

  // Filter documents based on search term and types
  useEffect(() => {
    let filtered = allDocuments;
    
    if (documentSearchTerm) {
      filtered = filtered.filter(doc => 
        doc.name?.toLowerCase().includes(documentSearchTerm.toLowerCase())
      );
    }
    
    if (selectedDocumentTypes.length > 0) {
      filtered = filtered.filter(doc => 
        selectedDocumentTypes.includes(doc.type?.toLowerCase())
      );
    }
    
    setFilteredDocuments(filtered);
  }, [allDocuments, documentSearchTerm, selectedDocumentTypes]);

  const handleSaveCriteria = () => {
    // Generate an ID from the name if empty
    const id = criteriaName.trim().toLowerCase().replace(/\s+/g, '_');
    
    if (id === '') {
      // Show error message or validation
      return;
    }

    const criteriaItem = {
      id,
      name: criteriaName,
      type: "prompt",
      conversation_goal_prompt: criteriaPrompt,
      use_knowledge_base: useKnowledgeBase
    };

    if (editingCriteria) {
      // Update existing criteria
      const updatedCriteria = evaluationCriteria.map(item => 
        item.id === editingCriteria.id ? criteriaItem : item
      );
      setEvaluationCriteria(updatedCriteria);
    } else {
      // Add new criteria
      setEvaluationCriteria([...evaluationCriteria, criteriaItem]);
    }

    setHasUnsavedChanges(true);
    closeCriteriaModal();
  };

  // Knowledge base helper functions
  const getDocumentTypeInfo = (type) => {
    switch (type?.toLowerCase()) {
      case 'url':
        return { icon: Link, color: 'text-chart-4', bgColor: 'bg-chart-4/10' };
      case 'file':
        return { icon: File, color: 'text-chart-1', bgColor: 'bg-chart-1/10' };
      case 'text':
        return { icon: FileText, color: 'text-chart-2', bgColor: 'bg-chart-2/10' };
      default:
        return { icon: BookOpen, color: 'text-muted-foreground', bgColor: 'bg-muted' };
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleRemoveDocument = async (document) => {
    try {
      // Remove document from agent's knowledge base (local state only)
      const updatedDocuments = knowledgeBaseDocuments.filter(doc => doc.id !== document.id);
      setKnowledgeBaseDocuments(updatedDocuments);
    } catch (err) {
      console.error('Failed to remove document:', err);
      toast.error('Failed to remove document. Please try again.');
    }
  };

  const handleAddDocumentToAgent = async (document) => {
    try {
      // Add document to agent's knowledge base (local state only)
      const updatedDocuments = [...knowledgeBaseDocuments, document];
      setKnowledgeBaseDocuments(updatedDocuments);
      
      setAddDocumentOpen(false);
    } catch (err) {
      console.error('Failed to add document to agent:', err);
      toast.error('Failed to add document to agent. Please try again.');
    }
  };


  // Multi-selection handlers
  const handleToggleDocumentType = (type) => {
    setSelectedDocumentTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };


  const documentTypes = [
    { value: 'url', label: 'URL', icon: Link, color: 'text-chart-4', bgColor: 'bg-chart-4/10' },
    { value: 'file', label: 'File', icon: File, color: 'text-chart-1', bgColor: 'bg-chart-1/10' },
    { value: 'text', label: 'Text', icon: FileText, color: 'text-chart-2', bgColor: 'bg-chart-2/10' }
  ];

  // Validation functions
  const validateUrlForm = () => {
    const errors = {};
    
    if (!documentName.trim()) {
      errors.documentName = 'Document name is required';
    }
    
    if (!documentUrl.trim()) {
      errors.documentUrl = 'URL is required';
    } else {
      // Basic URL validation
      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(documentUrl.trim())) {
        errors.documentUrl = 'Please enter a valid URL (starting with http:// or https://)';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateTextForm = () => {
    const errors = {};
    
    if (!documentName.trim()) {
      errors.documentName = 'Document name is required';
    }
    
    if (!documentText.trim()) {
      errors.documentText = 'Text content is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateFileForm = () => {
    const errors = {};
    
    if (selectedFiles.length === 0) {
      errors.selectedFiles = 'Please select at least one file to upload';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle URL submission
  const handleAddUrl = async () => {
    if (!validateUrlForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await knowledgebaseService.createDocumentFromUrl({
        name: documentName.trim(),
        url: documentUrl.trim()
      });
      
      // Add the new document to agent's knowledge base (local state only)
      const newDocument = response.document || {
        id: response.id || Date.now().toString(),
        name: documentName.trim(),
        type: 'url',
        url: documentUrl.trim(),
        created_at: Date.now() / 1000
      };
      
      const updatedDocuments = [...knowledgeBaseDocuments, newDocument];
      setKnowledgeBaseDocuments(updatedDocuments);
      
      // Refresh all documents list for the popover
      await fetchAllDocuments();
      
      // Reset form and close dialog
      setDocumentName('');
      setDocumentUrl('');
      setFormErrors({});
      setAddUrlDialogOpen(false);
      
      // Show success notification
      toast.success('URL document created. Click Save to update the agent.');
    } catch (err) {
      console.error('Failed to add URL:', err);
      toast.error('Failed to add URL document. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Text submission
  const handleAddText = async () => {
    if (!validateTextForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await knowledgebaseService.createDocumentFromText({
        name: documentName.trim(),
        text: documentText.trim()
      });
      
      // Add the new document to agent's knowledge base (local state only)
      const newDocument = response.document || {
        id: response.id || Date.now().toString(),
        name: documentName.trim(),
        type: 'text',
        text: documentText.trim(),
        created_at: Date.now() / 1000
      };
      
      const updatedDocuments = [...knowledgeBaseDocuments, newDocument];
      setKnowledgeBaseDocuments(updatedDocuments);
      
      // Refresh all documents list for the popover
      await fetchAllDocuments();
      
      // Reset form and close dialog
      setDocumentName('');
      setDocumentText('');
      setFormErrors({});
      setAddTextDialogOpen(false);
      
      // Show success notification
      toast.success('Text document created. Click Save to update the agent.');
    } catch (err) {
      console.error('Failed to add text:', err);
      toast.error('Failed to create text document. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle File submission
  const handleAddFile = async () => {
    if (!validateFileForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Upload each file separately
      const uploadPromises = selectedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('name', file.name);
        formData.append('file', file);
        
        return knowledgebaseService.createDocumentFromFile(formData);
      });
      
      const responses = await Promise.all(uploadPromises);
      
      // Add the new documents to agent's knowledge base (local state only)
      const newDocuments = responses.map((response, index) => {
        const file = selectedFiles[index];
        return response.document || {
          id: response.id || Date.now().toString() + index,
          name: file.name,
          type: 'file',
          filename: file.name,
          created_at: Date.now() / 1000
        };
      });
      
      const updatedDocuments = [...knowledgeBaseDocuments, ...newDocuments];
      setKnowledgeBaseDocuments(updatedDocuments);
      
      // Refresh all documents list for the popover
      await fetchAllDocuments();
      
      // Reset form and close dialog
      setSelectedFiles([]);
      setFormErrors({});
      setAddFilesDialogOpen(false);
      
      // Show success notification
      toast.success(`${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''} uploaded. Click Save to update the agent.`);
    } catch (err) {
      console.error('Failed to add files:', err);
      toast.error('Failed to upload files. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle removing individual files
  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSave = async () => {
    setIsSaving(true);
    setToastSaveState('loading');

    try {
      // Create a deep copy of the original agent structure from getAgentDetails
      const payload = JSON.parse(JSON.stringify(agent));

      // Remove agent_id from the payload since it should only be in the URL
      delete payload.agent_id;

      // Helper function to safely set nested properties
      const setNestedProperty = (obj, path, value) => {
        const parts = path.split('.');
        let current = obj;
        
        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i];
          if (!(part in current)) {
            current[part] = {};
          }
          current = current[part];
        }
        
        current[parts[parts.length - 1]] = value;
      };

      // Update only the values that have been changed by the user
      
      // Agent language
      setNestedProperty(payload, 'conversation_config.agent.language', defaultLanguage);
      
      // Additional languages as language_presets
      const languagePresets = {};
      
      // Add additional languages to presets
      if (Array.isArray(additionalLanguages)) {
        additionalLanguages.forEach(langCode => {
          if (langCode && typeof langCode === 'string') {
            languagePresets[langCode] = {
              overrides: {
                agent: {
                  first_message: firstMessage
                },
                tts: null
              },
              first_message_translation: {
                source_hash: JSON.stringify({
                  firstMessage: firstMessage,
                  language: langCode
                }),
                text: firstMessage // Ideally this would be translated, but we'll use the same text for now
              }
            };
          }
        });
      }
      
      // Make sure to include the default language in presets if not already there
      if (Object.keys(languagePresets).length > 0 && !languagePresets[defaultLanguage]) {
        languagePresets[defaultLanguage] = {
          overrides: {
            agent: {
              first_message: firstMessage
            },
            tts: null
          },
          first_message_translation: {
            source_hash: JSON.stringify({
              firstMessage: firstMessage,
              language: defaultLanguage
            }),
            text: firstMessage
          }
        };
      }
      
      // Only set language_presets if we have any languages
      if (Object.keys(languagePresets).length > 0) {
        setNestedProperty(payload, 'conversation_config.language_presets', languagePresets);
      }
      
      // First message
      setNestedProperty(payload, 'conversation_config.agent.first_message', firstMessage);
      
      // System prompt
      setNestedProperty(payload, 'conversation_config.agent.prompt.prompt', systemPrompt);
      
      // LLM model
      setNestedProperty(payload, 'conversation_config.agent.prompt.llm', llm);
      
      // Temperature
      setNestedProperty(payload, 'conversation_config.agent.prompt.temperature', temperature);
      
      // Token limit
      setNestedProperty(payload, 'conversation_config.agent.prompt.max_tokens', tokenLimit);
      
      // Tools - combine system tools and custom tools
      const systemTools = [];
      
      if (endCallEnabled) {
        systemTools.push({
          type: "system",
          name: "end_call",
          description: "",
          response_timeout_secs: 20,
          disable_interruptions: false,
          force_pre_tool_speech: false,
          assignments: [],
          params: {
            system_tool_type: "end_call"
          }
        });
      }
      
      if (detectLanguageEnabled) {
        systemTools.push({
          type: "system",
          name: "language_detection",
          description: "",
          response_timeout_secs: 20,
          disable_interruptions: false,
          force_pre_tool_speech: false,
          assignments: [],
          params: {
            system_tool_type: "language_detection"
          }
        });
      }
      
      if (skipTurnEnabled) {
        systemTools.push({
          type: "system",
          name: "skip_turn",
          description: "",
          response_timeout_secs: 20,
          disable_interruptions: false,
          force_pre_tool_speech: false,
          assignments: [],
          params: {
            system_tool_type: "skip_turn"
          }
        });
      }
      
      if (playKeypadTouchToneEnabled) {
        systemTools.push({
          type: "system",
          name: "play_keypad_touch_tone",
          description: "",
          response_timeout_secs: 20,
          disable_interruptions: false,
          force_pre_tool_speech: false,
          assignments: [],
          params: {
            system_tool_type: "play_keypad_touch_tone"
          }
        });
      }
      
      // Combine system tools with custom tools (excluding system tools from custom tools array)
      const customTools = tools.filter(tool => tool.type !== 'system');
      const formattedCustomTools = customTools.map(tool => ({
        ...tool,
        type: typeof tool.type === 'string' ? tool.type.toLowerCase() : tool.type
      }));
      
      const allTools = [...systemTools, ...formattedCustomTools];
      setNestedProperty(payload, 'conversation_config.agent.prompt.tools', allTools);
      
      // Knowledge base documents
      setNestedProperty(payload, 'conversation_config.agent.prompt.knowledge_base', knowledgeBaseDocuments);
      
      // Voice settings
      setNestedProperty(payload, 'conversation_config.tts.voice_id', selectedVoice.id);
      
      // Set TTS output format
      if (ttsOutputFormat) {
        setNestedProperty(payload, 'conversation_config.tts.agent_output_audio_format', normalizeTTSFormat(ttsOutputFormat));
      }
      
      // Set voice optimization settings
      setNestedProperty(payload, 'conversation_config.tts.optimize_streaming_latency', optimizeStreamingLatency);
      setNestedProperty(payload, 'conversation_config.tts.stability', stability);
      setNestedProperty(payload, 'conversation_config.tts.speed', speed);
      setNestedProperty(payload, 'conversation_config.tts.similarity_boost', similarityBoost);
      
      // Set appropriate TTS model based on language
      // English uses eleven_turbo_v2, other languages use eleven_flash_v2_5
      setNestedProperty(payload, 'conversation_config.tts.model_id', defaultLanguage === 'en' ? 'eleven_turbo_v2' : 'eleven_flash_v2_5');
      
      // Ensure all tool types are lowercase throughout the payload
      if (payload.conversation_config?.agent?.prompt?.tools) {
        payload.conversation_config.agent.prompt.tools = 
          payload.conversation_config.agent.prompt.tools.map(tool => ({
            ...tool,
            type: typeof tool.type === 'string' ? tool.type.toLowerCase() : tool.type
          }));
      }
      
      // Authentication
      setNestedProperty(payload, 'platform_settings.auth.enable_auth', authEnabled);
      
      // Allowlist
      setNestedProperty(payload, 'platform_settings.auth.allowlist', allowedDomains.map(domain => {
        // If the domain is already an object with hostname, keep it that way
        if (typeof domain === 'string') {
          return { hostname: domain };
        }
        return domain;
      }));
      
      // Overrides - maintain proper structure
      if (!payload.platform_settings) {
        payload.platform_settings = {};
      }
      
      if (!payload.platform_settings.overrides) {
        payload.platform_settings.overrides = {
          enable_conversation_initiation_client_data_from_webhook: false,
          custom_llm_extra_body: false,
          conversation_config_override: {
            agent: {
              prompt: {
                prompt: false
              },
              first_message: false,
              language: true
            },
            tts: {
              voice_id: false
            }
          }
        };
      } else {
        // Ensure the conversation_config_override structure is correct
        if (!payload.platform_settings.overrides.conversation_config_override) {
          payload.platform_settings.overrides.conversation_config_override = {
            agent: {
              prompt: {
                prompt: false
              },
              first_message: false,
              language: true
            },
            tts: {
              voice_id: false
            }
          };
        }
      }
      
      // Set evaluation criteria
      if (!payload.platform_settings) {
        payload.platform_settings = {};
      }
      if (!payload.platform_settings.evaluation) {
        payload.platform_settings.evaluation = {};
      }
      payload.platform_settings.evaluation.criteria = evaluationCriteria;
      
      // Set data collection
      payload.platform_settings.data_collection = dataCollection;
      
      // Set call limits and overrides
      setNestedProperty(payload, 'platform_settings.call_limits.agent_concurrency_limit', agentConcurrencyLimit);
      setNestedProperty(payload, 'platform_settings.call_limits.daily_limit', dailyCallsLimit);
      setNestedProperty(payload, 'platform_settings.overrides.conversation_config_override.agent.language', overrideAgentLanguage);
      setNestedProperty(payload, 'platform_settings.overrides.conversation_config_override.agent.prompt.prompt', overrideSystemPrompt);
      setNestedProperty(payload, 'platform_settings.overrides.conversation_config_override.agent.first_message', overrideFirstMessage);
      setNestedProperty(payload, 'platform_settings.overrides.conversation_config_override.tts.voice_id', overrideVoice);
      
      // Use the PATCH endpoint
      await agentsService.updateAgent(agentId, payload);
      setHasUnsavedChanges(false);
      // Update the original values after successful save
      setOriginalEvaluationCriteria(evaluationCriteria);
      setOriginalDataCollection(dataCollection);
      setToastSaveState('success');

      // Reset to initial state after showing success
      setTimeout(() => {
        setToastSaveState('initial');
      }, 2000);

      toast.success('Agent updated successfully!', {
        duration: 4000
      });
    } catch (err) {
      setToastSaveState('initial');

      toast.error('Failed to update agent. Please try again.', {
        duration: 6000,
        action: {
          label: 'Retry',
          onClick: () => handleSave()
        }
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearChanges = () => {
    // Reset to original values
    if (agent) {
      setLlm(agent.conversation_config?.agent?.prompt?.llm || 'gpt-4o-mini');
      setTemperature(agent.conversation_config?.agent?.prompt?.temperature ?? 0.5);
      setTokenLimit(agent.conversation_config?.agent?.prompt?.max_tokens ?? -1);
      setDefaultLanguage(agent.conversation_config?.agent?.language || 'en');
      
      // Reset additional languages from language_presets
      if (agent.conversation_config?.language_presets) {
        setAdditionalLanguages(extractAdditionalLanguages(
          agent.conversation_config.language_presets,
          agent.conversation_config?.agent?.language || 'en'
        ));
      } else {
        setAdditionalLanguages([]);
      }
      
      setFirstMessage(agent.conversation_config?.agent?.first_message || 'Hey there, I\'m Alexis from 44agents support. How can I help you today?');
      setSystemPrompt(agent.conversation_config?.agent?.prompt?.prompt || 'You\'re attentive and adaptive');
      
      // Reset tools and system tool toggles
      if (agent.conversation_config?.agent?.prompt?.tools) {
        const allTools = agent.conversation_config.agent.prompt.tools;
        
        // Separate system tools from custom tools
        const systemTools = allTools.filter(tool => tool.type === 'system');
        const customTools = allTools.filter(tool => tool.type !== 'system');
        
        // Set custom tools
        setTools(customTools);
        
        // Set system tool toggles based on what's in the tools array
        setEndCallEnabled(systemTools.some(tool => tool.name === 'end_call'));
        setDetectLanguageEnabled(systemTools.some(tool => tool.name === 'language_detection'));
        setSkipTurnEnabled(systemTools.some(tool => tool.name === 'skip_turn'));
        setPlayKeypadTouchToneEnabled(systemTools.some(tool => tool.name === 'play_keypad_touch_tone'));
      } else {
        setTools([]);
        // Default system tool states
        setEndCallEnabled(false);
        setDetectLanguageEnabled(false);
        setSkipTurnEnabled(false);
        setPlayKeypadTouchToneEnabled(false);
      }
      
      // Reset voice optimization settings
      setOptimizeStreamingLatency(agent.conversation_config?.tts?.optimize_streaming_latency ?? 0);
      setStability(agent.conversation_config?.tts?.stability ?? 0);
      setSpeed(agent.conversation_config?.tts?.speed ?? 0);
      setSimilarityBoost(agent.conversation_config?.tts?.similarity_boost ?? 0);
      
      // Reset auth and allowlist settings
      setAuthEnabled(!!agent.platform_settings?.auth?.enable_auth);
      setEnableOverrides(!!agent.platform_settings?.overrides?.conversation_config_override);
      
      // Reset TTS output format
      setTtsOutputFormat(normalizeTTSFormat(agent.conversation_config?.tts?.agent_output_audio_format));
      
      // Reset allowlist from platform_settings
      if (agent.platform_settings?.auth?.allowlist) {
        setAllowedDomains(normalizeDomains(agent.platform_settings.auth.allowlist));
      } else {
        setAllowedDomains(['*']);
      }
      
      // Reset evaluation criteria
      if (agent.platform_settings?.evaluation?.criteria && Array.isArray(agent.platform_settings.evaluation.criteria)) {
        setEvaluationCriteria(agent.platform_settings.evaluation.criteria);
      } else {
        setEvaluationCriteria([]);
      }
      
      // Reset data collection
      if (agent.platform_settings?.data_collection && typeof agent.platform_settings.data_collection === 'object') {
        setDataCollection(agent.platform_settings.data_collection);
      } else {
        setDataCollection({});
      }
      
      // Reset call limits and overrides
      setAgentConcurrencyLimit(agent.platform_settings?.call_limits?.agent_concurrency_limit ?? -1);
      setDailyCallsLimit(agent.platform_settings?.call_limits?.daily_limit ?? 100000);
      const clearOverrides = agent.platform_settings?.overrides?.conversation_config_override || {};
      setOverrideAgentLanguage(!!clearOverrides.agent?.language);
      setOverrideSystemPrompt(!!clearOverrides.agent?.prompt?.prompt);
      setOverrideFirstMessage(!!clearOverrides.agent?.first_message);
      setOverrideVoice(!!clearOverrides.tts?.voice_id);
      // Add state for advanced call limits
      setTurnTimeout(agent.conversation_config?.turn?.turn_timeout ?? 7);
      setSilenceEndCallTimeout(agent.conversation_config?.turn?.silence_end_call_timeout ?? -1);
      setMaxConversationDuration(agent.conversation_config?.conversation?.max_duration_seconds ?? 300);
      
      setHasUnsavedChanges(false);
    }
  };

  // Add click outside handler
  const handleClickOutside = useCallback((event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      closeCriteriaModal();
    }
  }, []);

  // Add useEffect for modal click outside detection
  useEffect(() => {
    if (showCriteriaModal) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCriteriaModal, handleClickOutside]);



  const openEditDataCollectionModal = (key, item) => {
    document.body.style.overflow = 'hidden'; // Prevent scrolling
    setEditingDataItem({ key, ...item });
    setDataItemIdentifier(key);
    setDataItemType(item.type || "string");
    setDataItemDescription(item.description || "");
    setShowDataCollectionModal(true);
  };

  const closeDataCollectionModal = () => {
    document.body.style.overflow = ''; // Restore scrolling
    setShowDataCollectionModal(false);
  };

  // Handle click outside data collection modal
  const handleDataCollectionClickOutside = useCallback((event) => {
    if (dataCollectionModalRef.current && !dataCollectionModalRef.current.contains(event.target)) {
      closeDataCollectionModal();
    }
  }, []);

  // Add useEffect for data collection modal click outside detection
  useEffect(() => {
    if (showDataCollectionModal) {
      document.addEventListener('mousedown', handleDataCollectionClickOutside);
    } else {
      document.removeEventListener('mousedown', handleDataCollectionClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleDataCollectionClickOutside);
    };
  }, [showDataCollectionModal, handleDataCollectionClickOutside]);

  // Handle saving data collection item
  const handleSaveDataCollectionItem = () => {
    const identifier = dataItemIdentifier.trim();
    
    if (identifier === '') {
      // Show error message or validation
      return;
    }

    const dataItem = {
      type: dataItemType.toLowerCase(),
      description: dataItemDescription
    };

    if (editingDataItem) {
      // Update existing item
      const updatedCollection = { ...dataCollection };
      delete updatedCollection[editingDataItem.key]; // Remove the old key if it was renamed
      updatedCollection[identifier] = dataItem;
      setDataCollection(updatedCollection);
    } else {
      // Add new item
      setDataCollection({
        ...dataCollection,
        [identifier]: dataItem
      });
    }

    setHasUnsavedChanges(true);
    closeDataCollectionModal();
  };

  // Handler functions for new buttons
  const handleTalkToAgent = () => {
    // Navigate to the talk to agent page
    navigate(`/agents/${agentId}/talk`);
  };

  const handleCopyAgentId = async () => {
    try {
      await navigator.clipboard.writeText(agentId);
      toast.success('Agent ID copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy Agent ID');
    }
  };

  const handleDuplicateAgent = () => {
    // Navigate to create agent with this agent as template
    navigate(`/agents/create?template=${agentId}`);
  };

  const handleDeleteAgent = () => {
    // Show confirmation dialog and delete agent
    if (window.confirm('Are you sure you want to delete this agent? This action cannot be undone.')) {
      agentsService.deleteAgent(agentId)
        .then(() => {
          toast.success('Agent deleted successfully');
          navigate('/agents');
        })
        .catch((error) => {
          toast.error('Failed to delete agent');
        });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/50">
        {/* Header skeleton */}
        <div className="bg-card border-b border-border/60">
          <div className="flex h-20 items-center justify-between px-8 max-w-4xl mx-auto">
            <div className="flex items-center space-x-2">
              <Skeleton className="h-4 w-12" />
              <span>/</span>
              <Skeleton className="h-4 w-20" />
            </div>
            <Skeleton className="h-8 w-20" />
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-8 space-y-6">
          {/* Agent title skeleton */}
          <div className="flex items-center justify-between">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-10 w-24" />
          </div>
          
          {/* Tabs skeleton */}
          <div className="space-y-4">
            <div className="flex space-x-2">
              <Skeleton className="h-10 w-16" />
              <Skeleton className="h-10 w-16" />
              <Skeleton className="h-10 w-20" />
              <Skeleton className="h-10 w-24" />
            </div>
            
            {/* Content area skeleton */}
            <div className="space-y-6">
              <Card className="border border-border bg-card shadow-none">
                <CardHeader>
                  <Skeleton className="h-6 w-32" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-32 w-full" />
                  </div>
                </CardContent>
              </Card>
              
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <Skeleton className="h-6 w-28" />
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-6 w-full" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center">
        <Card className="border border-border bg-card shadow-none max-w-md">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold text-destructive mb-4">
              Error loading agent
            </h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/agents')} className="bg-primary hover:bg-primary/90">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Agents
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/50">
      {/* Header with enhanced styling */}
      <div className="bg-sidebar border-b border-border/60 sticky top-0 z-10">
        <div className="flex h-16 items-center justify-between px-8">
          {/* Left side - Breadcrumb aligned to left edge */}
          <div className="flex items-center">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    asChild
                    className="text-sm font-medium hover:text-primary cursor-pointer text-muted-foreground"
                  >
                    <button onClick={() => navigate('/agents')}>
                      Agents
                    </button>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-muted-foreground" />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-sm font-medium text-card-foreground">
                    {agent?.name || 'Agent Details'}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          {/* Right side - Action buttons aligned to right edge */}
          <div className="flex items-center gap-2">
            {/* Talk to Agent button */}
            <StarBorder as="button" onClick={handleTalkToAgent} className="text-nowrap">
              <span className="flex items-center gap-1 text-sm font-medium">
                <Mic className="w-3 h-3" />
                Talk to {agent?.name || 'Agent'}
              </span>
            </StarBorder>

            {/* Options dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-card-foreground hover:bg-muted border-0 hover:border-0 focus:border-0 active:border-0 outline-0 focus:outline-0 ring-0 focus:ring-0 focus-visible:ring-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate(`/conversations?agent=${agentId}`)}>
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Conversations
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleCopyAgentId}>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy Agent ID
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDuplicateAgent}>
                  <Copy className="mr-2 h-4 w-4" />
                  Duplicate Agent
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDeleteAgent} className="text-destructive focus:text-destructive">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Agent
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-8 space-y-6">
        {/* Agent Name and ID */}
        <div className="space-y-1">
          <h1 className="text-2xl font-bold text-card-foreground">{agent?.name || 'Agent Details'}</h1>
          <p className="text-muted-foreground font-mono text-sm">{agentId}</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6 bg-transparent border-0 shadow-none p-0">
            <TabsTrigger
              value="agent"
              className="text-xs font-medium data-[state=active]:text-accent-foreground data-[state=inactive]:text-muted-foreground data-[state=active]:bg-transparent data-[state=inactive]:bg-transparent border-0 shadow-none"
            >
              Agent
            </TabsTrigger>
            <TabsTrigger
              value="voice"
              className="text-xs font-medium data-[state=active]:text-accent-foreground data-[state=inactive]:text-muted-foreground data-[state=active]:bg-transparent data-[state=inactive]:bg-transparent border-0 shadow-none"
            >
              Voice
            </TabsTrigger>
            <TabsTrigger
              value="analysis"
              className="text-xs font-medium data-[state=active]:text-accent-foreground data-[state=inactive]:text-muted-foreground data-[state=active]:bg-transparent data-[state=inactive]:bg-transparent border-0 shadow-none"
            >
              Analysis
            </TabsTrigger>
            <TabsTrigger
              value="security"
              className="text-xs font-medium data-[state=active]:text-accent-foreground data-[state=inactive]:text-muted-foreground data-[state=active]:bg-transparent data-[state=inactive]:bg-transparent border-0 shadow-none"
            >
              Security
            </TabsTrigger>
            <TabsTrigger
              value="advanced"
              className="text-xs font-medium data-[state=active]:text-accent-foreground data-[state=inactive]:text-muted-foreground data-[state=active]:bg-transparent data-[state=inactive]:bg-transparent border-0 shadow-none"
            >
              Advanced
            </TabsTrigger>
            {/* <TabsTrigger
              value="widget"
              className="text-xs font-medium data-[state=active]:text-accent-foreground data-[state=inactive]:text-muted-foreground data-[state=active]:bg-transparent data-[state=inactive]:bg-transparent border-0 shadow-none"
            >
              Widget
            </TabsTrigger> */}
          </TabsList>

          {/* Tab Content */}
          <TabsContent value="agent" className="space-y-6 mt-6">
            {/* Agent Language */}
            <Card className="border border-border bg-card shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold text-card-foreground">
                  Agent Language
                </CardTitle>
                <p className="text-sm text-muted-foreground">Choose the default language the agent will communicate in.</p>
              </CardHeader>
              <CardContent>
              <Select value={defaultLanguage} onValueChange={setDefaultLanguage}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a language" />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(lang => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.flag} {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </CardContent>
            </Card>

            {/* Additional Languages */}
            <Card className="border border-border bg-card shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold text-card-foreground">
                  Additional Languages
                </CardTitle>
                <p className="text-sm text-muted-foreground">Specify additional languages which callers can choose from.</p>
              </CardHeader>
              <CardContent>
              <Select value="" onValueChange={(value) => {
                if (value && !additionalLanguages.includes(value)) {
                  setAdditionalLanguages([...additionalLanguages, value]);
                }
              }}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Add additional languages" />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.filter(lang =>
                    lang &&
                    lang.value &&
                    defaultLanguage &&
                    Array.isArray(additionalLanguages) &&
                    lang.value !== defaultLanguage &&
                    !additionalLanguages.includes(lang.value)
                  ).map(lang => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.flag} {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {additionalLanguages.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {additionalLanguages.map(langCode => {
                    const lang = LANGUAGES.find(l => l.value === langCode);
                    return (
                      <div key={langCode} className="flex items-center bg-accent text-accent-foreground rounded-full px-2 py-1 border border-accent hover:bg-accent/80 transition-all">
                        <span className="mr-1 text-sm">{lang?.flag}</span>
                        <span className="text-xs font-medium">{lang?.label}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="ml-1 h-auto p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => {
                            if (Array.isArray(additionalLanguages) && additionalLanguages.length > 0) {
                              setAdditionalLanguages(additionalLanguages.filter(l => l !== langCode));
                            }
                          }}
                          aria-label={`Remove ${lang?.label}`}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
              {additionalLanguages.length === 0 && (
                <div className="mt-3 text-sm text-muted-foreground">
                  No additional languages selected. Agent will only communicate in {LANGUAGES.find(l => l.value === defaultLanguage)?.label}.
                </div>
              )}
              </CardContent>
            </Card>
            
            {/* First message */}
            <Card className="border border-border bg-card shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold text-card-foreground">First message</CardTitle>
                <p className="text-sm text-muted-foreground">The first message the agent will say. If empty, the agent will wait for the user to start the conversation.</p>
              </CardHeader>
              <CardContent>
                <Textarea
                  className="resize-none border-input focus:border-primary focus:ring-2 focus:ring-primary/20"
                  rows={4}
                  value={firstMessage}
                  onChange={e => setFirstMessage(e.target.value)}
                  placeholder="Enter the first message..."
                />
              </CardContent>
            </Card>

            {/* System prompt */}
            <Card className="border border-border bg-card shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold text-card-foreground">System prompt</CardTitle>
                <p className="text-sm text-muted-foreground">The system prompt is used to determine the persona of the agent and the context of the conversation.</p>
              </CardHeader>
              <CardContent>
                <Textarea
                  className="resize-none"
                  rows={6}
                  value={systemPrompt}
                  onChange={e => setSystemPrompt(e.target.value)}
                />
              </CardContent>
            </Card>

          {/* Dynamic Variables */}
          <Card className="border border-border bg-card shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-card-foreground">Dynamic Variables</CardTitle>
              <p className="text-sm text-muted-foreground">
                Variables like <code className="bg-muted px-2 py-1 rounded font-mono text-xs">{'{{user_name}}'}</code> in your prompts and first message will be replaced with actual values when the conversation starts.
              </p>
            </CardHeader>
          </Card>
            
          {/* LLM Model */}
          <Card className="border border-border bg-card shadow-none">
            <CardContent className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="font-semibold text-lg text-card-foreground mb-1">LLM</div>
                <div className="text-muted-foreground text-sm mb-2 max-w-md">
                  Select which provider and model to use for the LLM.<br />
                  If your chosen LLM is not available at the moment or something goes wrong, we will redirect the conversation to another LLM.<br />
                  Currently, the LLM cost is covered by us. In the future, <span className="font-semibold">this cost will be passed onto you.</span>
                </div>
              </div>
              <div className="md:w-56">
                <Select value={llm} onValueChange={setLlm}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select LLM model" />
                  </SelectTrigger>
                  <SelectContent>
                    {LLM_MODELS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
            
          {/* Temperature */}
          <Card className="border border-border bg-card shadow-none">
            <CardContent className="p-6">
              <div className="font-semibold text-lg text-card-foreground mb-1">Temperature</div>
              <div className="text-muted-foreground text-sm mb-4">Temperature is a parameter that controls the creativity or randomness of the responses generated by the LLM.</div>
              <div className="space-y-3">
                <Slider
                  value={[temperature]}
                  onValueChange={(value) => setTemperature(value[0])}
                  max={1}
                  min={0}
                  step={0.01}
                  className="w-full"
                />
                <div className="text-sm font-semibold text-muted-foreground text-right">{temperature.toFixed(2)}</div>
              </div>
            </CardContent>
          </Card>
            
          {/* Limit token usage */}
          <Card className="border border-border bg-card shadow-none">
            <CardContent className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="font-semibold text-card-foreground mb-1">Limit token usage</div>
                <div className="text-muted-foreground text-sm">Configure the maximum number of tokens that the LLM can predict. A limit will be applied if the value is greater than 0.</div>
              </div>
              <div className="relative md:w-24">
            <Input
              type="number"
              className="w-full border-input focus:border-primary focus:ring-2 focus:ring-primary/20"
              value={tokenLimit}
              onChange={e => setTokenLimit(Number(e.target.value))}
              min="-1"
                  step="1"
            />
              </div>
            </CardContent>
          </Card>
            
          {/* Knowledge base */}
          <Card className="border border-border bg-card shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-card-foreground">Knowledge base</div>
                <Popover open={addDocumentOpen} onOpenChange={setAddDocumentOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="border-input text-card-foreground hover:bg-muted font-semibold">
                      Add document
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="end">
                    <div className="p-4">
                      {/* Search Bar */}
                      <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                        <Input
                          type="text"
                          placeholder="Search documents..."
                          value={documentSearchTerm}
                          onChange={(e) => setDocumentSearchTerm(e.target.value)}
                          className="pl-10 border-input focus:border-primary focus:ring-2 focus:ring-primary/20"
                        />
                      </div>

                      {/* Type Selection */}
                      <div className="mb-3">
                        <Popover open={typeSelectionOpen} onOpenChange={setTypeSelectionOpen}>
                          <PopoverTrigger asChild>
                            <div className="inline-flex items-center rounded-md px-2 h-8 transition-all duration-700 ease-out animate-in slide-in-from-left-5 cursor-pointer"
                                 style={{
                                   backgroundColor: selectedDocumentTypes.length > 0 ? 'var(--primary)' : 'transparent',
                                   border: selectedDocumentTypes.length > 0 ? '1px solid var(--primary)' : '1px solid var(--border)',
                                   color: selectedDocumentTypes.length > 0 ? 'var(--primary-foreground)' : 'var(--muted-foreground)'
                                 }}>
                              {selectedDocumentTypes.length > 0 ? (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedDocumentTypes([]);
                                    }}
                                    className="mr-1.5 text-primary-foreground hover:text-primary-foreground/80 transition-colors duration-300"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                  <span className="text-xs text-primary-foreground font-medium capitalize transition-all duration-500 ease-out">Type</span>
                                  <div className="mx-1.5 w-px h-4 bg-primary-foreground"></div>
                                  <span className="text-xs text-primary-foreground transition-all duration-500 ease-out animate-in slide-in-from-right-3">
                                    {selectedDocumentTypes.length === 1 
                                      ? documentTypes.find(t => t.value === selectedDocumentTypes[0])?.label
                                      : `${selectedDocumentTypes.length} types`
                                    }
                                  </span>
                                </>
                              ) : (
                                <>
                                  <Plus className="h-3 w-3 mr-1.5" />
                                  <span className="text-xs font-medium">Type</span>
                                </>
                              )}
                            </div>
                          </PopoverTrigger>
                          <PopoverContent className="w-48 p-2" align="start">
                            <div className="space-y-1">
                              {documentTypes.map((type) => {
                                const TypeIcon = type.icon;
                                const isSelected = selectedDocumentTypes.includes(type.value);
                                
                                return (
                                  <div
                                    key={type.value}
                                    className="flex items-center space-x-2 p-2 rounded-md hover:bg-muted cursor-pointer"
                                    onClick={() => handleToggleDocumentType(type.value)}
                                  >
                                    <div className={`w-5 h-5 ${type.bgColor} rounded flex items-center justify-center`}>
                                      <TypeIcon className={`w-2.5 h-2.5 ${type.color}`} />
                                    </div>
                                    <span className="text-xs font-medium">{type.label}</span>
                                    {isSelected && (
                                      <div className="ml-auto w-3 h-3 bg-primary rounded-full flex items-center justify-center">
                                        <svg className="w-1.5 h-1.5 text-primary-foreground" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>

                      <Separator className="mb-3" />

                      {/* Documents List */}
                      {allDocuments.length > 0 && (
                        <div className="space-y-2 max-h-60 overflow-y-auto mb-3">
                          {filteredDocuments.map((document) => {
                            const typeInfo = getDocumentTypeInfo(document.type);
                            const TypeIcon = typeInfo.icon;
                            
                            return (
                              <div
                                key={document.id}
                                className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                                onClick={() => handleAddDocumentToAgent(document)}
                              >
                                <div className="flex items-center space-x-3">
                                  <div className={`w-8 h-8 ${typeInfo.bgColor} rounded-lg flex items-center justify-center`}>
                                    <TypeIcon className={`w-4 h-4 ${typeInfo.color}`} />
                                  </div>
                                  <div>
                                    <div className="font-medium text-card-foreground text-sm">
                                      {document.name || 'Untitled Document'}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {document.id}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {allDocuments.length > 0 && <Separator className="mb-3" />}

                      {/* Document Type Options - Horizontal */}
                      <div className="grid grid-cols-3 gap-2">
                        <div
                          className="flex flex-col items-center p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => {
                            setAddDocumentOpen(false);
                            setAddUrlDialogOpen(true);
                          }}
                        >
                          <div className="w-8 h-8 bg-chart-4/10 rounded-lg flex items-center justify-center mb-2">
                            <Link className="w-4 h-4 text-chart-4" />
                          </div>
                          <div className="text-center">
                            <div className="font-medium text-card-foreground text-xs">
                              URL
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Web page
                            </div>
                          </div>
                        </div>
                        
                        <div
                          className="flex flex-col items-center p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => {
                            setAddDocumentOpen(false);
                            setAddFilesDialogOpen(true);
                          }}
                        >
                          <div className="w-8 h-8 bg-chart-1/10 rounded-lg flex items-center justify-center mb-2">
                            <File className="w-4 h-4 text-chart-1" />
                          </div>
                          <div className="text-center">
                            <div className="font-medium text-card-foreground text-xs">
                              File
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Upload
                            </div>
                          </div>
                        </div>
                        
                        <div
                          className="flex flex-col items-center p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => {
                            setAddDocumentOpen(false);
                            setAddTextDialogOpen(true);
                          }}
                        >
                          <div className="w-8 h-8 bg-chart-2/10 rounded-lg flex items-center justify-center mb-2">
                            <FileText className="w-4 h-4 text-chart-2" />
                          </div>
                          <div className="text-center">
                            <div className="font-medium text-card-foreground text-xs">
                              Text
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Create
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="text-muted-foreground text-sm mb-4">Provide the LLM with domain-specific information to help it answer questions more accurately.</div>
              
              {/* Display existing knowledge base documents */}
              {isLoadingKnowledgeBase ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, idx) => (
                    <div key={idx} className="flex items-center space-x-3 p-3 border border-border rounded-lg">
                      <Skeleton className="w-8 h-8 rounded-lg" />
                      <div className="flex-1">
                        <Skeleton className="h-4 w-3/4 mb-2" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                      <Skeleton className="w-8 h-8 rounded" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {knowledgeBaseDocuments.map((document) => {
                    const typeInfo = getDocumentTypeInfo(document.type);
                    const TypeIcon = typeInfo.icon;

                    return (
                      <div key={document.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 ${typeInfo.bgColor} rounded-lg flex items-center justify-center`}>
                            <TypeIcon className={`w-4 h-4 ${typeInfo.color}`} />
                          </div>
                          <div>
                            <div className="font-medium text-card-foreground text-sm">
                              {document.name || 'Untitled Document'}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Created {formatDate(document.created_at)}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveDocument(document)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

            </CardContent>
          </Card>

            {/* Tools */}
            <Card className="border border-border bg-card shadow-none">
              <CardContent className="p-6">
                <div className="mb-2">
                  <div className="font-semibold text-card-foreground">Tools</div>
                </div>
                <div className="text-muted-foreground text-sm mb-6">Let the agent perform specific actions.</div>
                
                {/* System Tools Section */}
                <div className="bg-muted border border-border rounded-lg p-4 mb-6">
                  <div className="space-y-4">
                    {/* End call */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-card-foreground font-medium">End call</Label>
                        <div className="text-sm text-muted-foreground">Gives agent the ability to end the call with the user.</div>
                </div>
                      <Switch
                        checked={endCallEnabled}
                        onCheckedChange={setEndCallEnabled}
                      />
                    </div>
                    
                    {/* Detect language */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-card-foreground font-medium">Detect language</Label>
                        <div className="text-sm text-muted-foreground">Gives agent the ability to change the language during conversation.</div>
                      </div>
                      <Switch
                        checked={detectLanguageEnabled}
                        onCheckedChange={setDetectLanguageEnabled}
                      />
                    </div>
                    
                    {/* Skip turn */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-card-foreground font-medium">Skip turn</Label>
                        <div className="text-sm text-muted-foreground">Agent will skip its turn if user explicitly indicates they need a moment.</div>
                      </div>
                      <Switch
                        checked={skipTurnEnabled}
                        onCheckedChange={setSkipTurnEnabled}
                      />
                    </div>
                    
                    {/* Play keypad touch tone */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-card-foreground font-medium">Play keypad touch tone</Label>
                        <div className="text-sm text-muted-foreground">Gives agent the ability to play keypad touch tones during a phone call.</div>
                      </div>
                      <Switch
                        checked={playKeypadTouchToneEnabled}
                        onCheckedChange={setPlayKeypadTouchToneEnabled}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Custom Tools Section */}
                {tools.length > 0 && (
                  <div className="border-t border-border pt-6">
                    <div className="font-medium text-card-foreground mb-4">Custom Tools</div>
                <div className="space-y-3">
                  {tools.map(tool => (
                    <div key={tool.id} className="border border-border rounded-lg p-4 hover:border-border transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="bg-muted p-2 rounded-lg text-muted-foreground">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                            </svg>
                          </div>
                          <div>
                            <div className="font-medium text-card-foreground">{tool.name}</div>
                            <div className="text-xs bg-muted text-card-foreground px-2 py-0.5 rounded inline-block mt-1">{tool.type}</div>
                          </div>
                        </div>
                        <button className="text-muted-foreground hover:text-destructive focus:outline-none transition-colors" aria-label={`Remove ${tool.name} tool`}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                      <div className="text-sm text-muted-foreground mt-2 ml-10">{tool.description}</div>
                    </div>
                  ))}
                    </div>
                </div>
              )}
              </CardContent>
            </Card>

            {/* Workspace Secrets */}
            <Card className="border border-border bg-card shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-card-foreground">Workspace Secrets</div>
                  <Button variant="outline" size="sm" className="border-input text-card-foreground hover:bg-muted font-semibold">Add secret</Button>
                </div>
                <div className="text-muted-foreground text-sm mb-4">Create and manage secure secrets that can be accessed across your workspace.</div>
              
              <div className="text-center py-6 border border-dashed border-input rounded-lg">
                <div className="text-muted-foreground text-xs mb-2">No secrets added yet</div>
                <button className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-accent-foreground bg-accent rounded-md hover:bg-accent/80 transition-colors">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add your first secret
                </button>
              </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="voice" className="space-y-6 mt-6">
            {/* Voice tab content placeholder */}
            <Card className="border border-border bg-card shadow-none">
              <CardContent className="p-6">
                <div className="font-semibold text-card-foreground mb-1">Voice</div>
                <div className="text-muted-foreground text-sm mb-4">Select the 44agents voice you want to use for the agent.</div>
              
              <Select
                value={selectedVoice?.id || ''}
                onValueChange={(voiceId) => {
                  const voice = voices.find(v => v.voice_id === voiceId);
                  if (voice) {
                    setSelectedVoice({ id: voice.voice_id, name: voice.name });
                  } else {
                    setSelectedVoice({ id: voiceId, name: `Voice ${voiceId.substring(0, 6)}...` });
                  }
                }}
                disabled={loadingVoices}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={loadingVoices ? "Loading voices..." : "Select a voice"} />
                </SelectTrigger>
                <SelectContent>
                  {voices.map(voice => (
                    <SelectItem key={voice.voice_id} value={voice.voice_id}>
                      {voice.name || `Voice ${voice.voice_id.substring(0, 6)}...`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </CardContent>
            </Card>
            
            {/* TTS output format */}
            <Card className="border border-border bg-card shadow-none">
              <CardContent className="p-6">
                <div className="font-semibold text-card-foreground mb-1">TTS output format</div>
                <div className="text-muted-foreground text-sm mb-4">
                  Select the output format you want to use for 44agents text to speech.
                </div>
              <Select value={ttsOutputFormat} onValueChange={setTtsOutputFormat}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select an output format" />
                </SelectTrigger>
                <SelectContent>
                  {TTS_OUTPUT_FORMATS.map(format => (
                    <SelectItem key={format.value} value={format.value}>
                      {format.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </CardContent>
            </Card>
            
            {/* Voice Optimization Settings */}
            <Card className="border border-border bg-card shadow-none">
              <CardContent className="p-6">
                <div className="font-semibold text-card-foreground mb-1">Optimize streaming latency <span className="text-muted-foreground">ⓘ</span></div>
                <div className="text-muted-foreground text-sm mb-4">
                  Configure latency optimizations for the speech generation. Latency can be optimized at the cost of quality.
                </div>
                <div className="space-y-3">
                  <Slider
                    value={[optimizeStreamingLatency || 0]}
                    onValueChange={(value) => setOptimizeStreamingLatency(value[0])}
                    max={4}
                    min={0}
                    step={1}
                    className="w-full"
                  />
                  <div className="text-xs text-muted-foreground text-right">{(optimizeStreamingLatency || 0).toFixed(2)}</div>
                </div>
              </CardContent>
            </Card>

            <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
              <div className="font-semibold text-card-foreground mb-1">Stability</div>
              <div className="text-muted-foreground text-sm mb-4">
                Higher values will make speech more consistent, but it can also make it sound monotone. Lower values will make speech sound more expressive, but may lead to instabilities.
              </div>
              <div className="space-y-3">
                <Slider
                  value={[stability || 0]}
                  onValueChange={(value) => setStability(value[0])}
                  max={1}
                  min={0}
                  step={0.05}
                  className="w-full"
                />
                <div className="text-xs text-muted-foreground text-right">{(stability || 0).toFixed(2)}</div>
              </div>
            </div>

            <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
              <div className="font-semibold text-card-foreground mb-1">Speed</div>
              <div className="text-muted-foreground text-sm mb-4">
                Controls the speed of the generated speech. Values below 1.0 will slow down the speech, while values above 1.0 will speed it up. Extreme values may affect the quality of the generated speech.
              </div>
              <div className="space-y-3">
                <Slider
                  value={[speed || 1]}
                  onValueChange={(value) => setSpeed(value[0])}
                  max={1.20}
                  min={0.70}
                  step={0.01}
                  className="w-full"
                />
                <div className="text-xs text-muted-foreground text-right">{(speed || 1).toFixed(2)}x</div>
              </div>
            </div>

            <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
              <div className="font-semibold text-card-foreground mb-1">Similarity</div>
              <div className="text-muted-foreground text-sm mb-4">
                Higher values will boost the overall clarity and consistency of the voice. Very high values may lead to artifacts. Adjusting this value to find the right balance is recommended.
              </div>
              <div className="space-y-3">
                <Slider
                  value={[similarityBoost || 0]}
                  onValueChange={(value) => setSimilarityBoost(value[0])}
                  max={1}
                  min={0}
                  step={0.05}
                  className="w-full"
                />
                <div className="text-xs text-muted-foreground text-right">{(similarityBoost || 0).toFixed(2)}</div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="analysis" className="space-y-6 mt-6">
            {/* Evaluation criteria */}
            <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-semibold text-card-foreground mb-1">Evaluation criteria</div>
                    <div className="text-muted-foreground text-sm">
                      Define custom criteria to evaluate conversations against. You can find the evaluation results for each conversation in <span className="text-card-foreground">the history tab</span>.
                    </div>
                  </div>
                  <Sheet open={showCriteriaModal} onOpenChange={setShowCriteriaModal}>
                    <SheetTrigger asChild>
                      <Button variant="outline" size="sm" className="border-input text-card-foreground hover:bg-muted font-semibold">
                        Add criteria
                      </Button>
                    </SheetTrigger>
                    <SheetContent className="w-[400px] sm:w-[540px]">
                      <SheetHeader>
                        <SheetTitle>{editingCriteria ? 'Edit criteria' : 'Add criteria'}</SheetTitle>
                        <SheetDescription>
                          Define custom criteria to evaluate conversations against.
                        </SheetDescription>
                      </SheetHeader>
                      <div className="mt-6">
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          handleSaveCriteria();
                        }} className="space-y-4">
                          <div>
                            <Label htmlFor="criteria-name" className="text-sm font-medium text-card-foreground mb-2 block">
                              Criteria name
                            </Label>
                            <Input
                              id="criteria-name"
                              type="text"
                              value={criteriaName}
                              onChange={(e) => setCriteriaName(e.target.value)}
                              placeholder="Enter criteria name"
                              required
                            />
                          </div>

                          <div>
                            <Label htmlFor="criteria-prompt" className="text-sm font-medium text-card-foreground mb-2 block">
                              Prompt
                            </Label>
                            <Textarea
                              id="criteria-prompt"
                              value={criteriaPrompt}
                              onChange={(e) => setCriteriaPrompt(e.target.value)}
                              rows={5}
                              placeholder="Enter prompt for the evaluation criteria"
                              required
                            />
                          </div>

                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="use-knowledge-base"
                              checked={useKnowledgeBase}
                              onCheckedChange={setUseKnowledgeBase}
                            />
                            <Label htmlFor="use-knowledge-base" className="text-sm text-card-foreground">
                              Use knowledge base when evaluating
                            </Label>
                          </div>

                          <div className="flex justify-end space-x-3 pt-4">
                            <Button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                closeCriteriaModal();
                              }}
                              variant="outline"
                              type="button"
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                            >
                              {editingCriteria ? 'Apply changes' : 'Add criteria'}
                            </Button>
                          </div>
                        </form>
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>
                
                {/* Display criteria list */}
                {evaluationCriteria.length > 0 ? (
                  <div className="space-y-2">
                    {evaluationCriteria.map((criteria) => (
                      <div 
                        key={criteria.id} 
                        className="border border-border rounded-lg p-4 flex items-start justify-between hover:border-border cursor-pointer"
                        onClick={() => openEditCriteriaModal(criteria)}
                      >
                        <div className="flex">
                          <div className="text-muted-foreground mr-3 mt-1">
                            <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div>
                            <div className="font-medium text-card-foreground">{criteria.id}</div>
                            <div className="text-muted-foreground text-sm">{criteria.conversation_goal_prompt}</div>
                          </div>
                        </div>
                        <button 
                          className="text-muted-foreground hover:text-destructive focus:outline-none transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCriteria(criteria.id);
                          }}
                          aria-label={`Delete ${criteria.id} criteria`}
                        >
                          <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Data collection */}
            <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-semibold text-card-foreground mb-1">Data collection</div>
                    <div className="text-muted-foreground text-sm">
                      Define custom data specifications to extract from conversation transcripts. You can find the evaluation results for each conversation in <span className="text-card-foreground">the history tab</span>.
                    </div>
                  </div>
                  <Sheet open={showDataCollectionModal} onOpenChange={setShowDataCollectionModal}>
                    <SheetTrigger asChild>
                      <Button variant="outline" size="sm" className="border-input text-card-foreground hover:bg-muted font-semibold">
                        Add item
                      </Button>
                    </SheetTrigger>
                    <SheetContent className="w-[400px] sm:w-[540px]">
                      <SheetHeader>
                        <SheetTitle>Add data collection item</SheetTitle>
                        <SheetDescription>
                          Configure what data to extract from conversations.
                        </SheetDescription>
                      </SheetHeader>
                      <div className="mt-6">
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          handleSaveDataCollectionItem();
                        }} className="space-y-4">
                          <div>
                            <Label htmlFor="data-type" className="text-sm font-medium text-card-foreground mb-2 block">
                              Type
                            </Label>
                            <Select value={dataItemType} onValueChange={setDataItemType}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select data type" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableTypes.map(type => (
                                  <SelectItem key={type.toLowerCase()} value={type.toLowerCase()}>
                                    {type}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="data-identifier" className="text-sm font-medium text-card-foreground mb-2 block">
                              Identifier
                            </Label>
                            <Input
                              id="data-identifier"
                              type="text"
                              value={dataItemIdentifier}
                              onChange={(e) => setDataItemIdentifier(e.target.value)}
                              placeholder="Enter identifier"
                              required
                            />
                          </div>

                          <div>
                            <Label htmlFor="data-description" className="text-sm font-medium text-card-foreground mb-2 block">
                              Description
                            </Label>
                            <Textarea
                              id="data-description"
                              value={dataItemDescription}
                              onChange={(e) => setDataItemDescription(e.target.value)}
                              rows={4}
                              placeholder="This prompt will be passed to the LLM and should describe in detail how to extract the data from the transcript."
                            />
                          </div>

                          <div className="flex justify-end space-x-3 pt-4">
                            <Button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                closeDataCollectionModal();
                              }}
                              variant="outline"
                              type="button"
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                            >
                              Add item
                            </Button>
                          </div>
                        </form>
                      </div>
                    </SheetContent>
                  </Sheet>
                </div>
                
                {/* Display data collection items */}
                {Object.keys(dataCollection).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(dataCollection).map(([key, item]) => (
                      <div 
                        key={key} 
                        className="border border-border rounded-lg p-4 flex items-start justify-between hover:border-border cursor-pointer"
                        onClick={() => openEditDataCollectionModal(key, item)}
                      >
                        <div className="flex">
                          <div className="text-muted-foreground mr-3 mt-1">
                            <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                          </div>
                          <div>
                            <div className="flex items-center">
                              <span className="font-medium text-card-foreground mr-2">{key}</span>
                              <span className="text-xs px-1.5 py-0.5 bg-muted text-card-foreground rounded">{item.type || 'string'}</span>
                            </div>
                            <div className="text-muted-foreground text-sm">{item.description || 'No description'}</div>
                          </div>
                        </div>
                        <button 
                          className="text-muted-foreground hover:text-destructive focus:outline-none transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDataCollectionItem(key);
                          }}
                          aria-label={`Delete ${key} data collection item`}
                        >
                          <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="security" className="space-y-6 mt-6">
            {/* Authentication */}
            <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-card-foreground mb-1">Enable authentication</div>
                  <div className="text-muted-foreground text-sm mb-2">
                    Require users to authenticate before connecting to the agent.
                  </div>
                  {authEnabled && (
                    <div className="text-sm text-amber-600 mt-2 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Authentication settings can be configured in the Authentication tab in your project settings.
                    </div>
                  )}
                </div>
                <Switch
                  checked={authEnabled}
                  onCheckedChange={setAuthEnabled}
                />
              </div>
            </div>

            {/* Allowlist */}
            <Card className="border border-border bg-card shadow-none">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-semibold text-card-foreground mb-1">Allowlist</div>
                    <div className="text-muted-foreground text-sm">
                      Specify the hosts that will be allowed to connect to this agent.
                    </div>
                  </div>
                <Button
                  onClick={() => {
                    setNewDomain('');
                    document.getElementById('domain-input').focus();
                  }}
                  size="sm"
                  variant="outline"
                  className="border-input text-card-foreground hover:bg-muted"
                >
                  Add
                </Button>
                </div>
              
              <div className="border border-border rounded-lg p-6">
                {allowedDomains.length === 1 && allowedDomains[0] === '*' ? (
                  <div className="text-card-foreground">
                    No allowlist specified. Any host will be able to connect to this agent.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {allowedDomains.map((domain, index) => (
                      <div key={`domain-${index}`} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-card-foreground">{typeof domain === 'string' ? domain : JSON.stringify(domain)}</span>
                        <button 
                          className="text-muted-foreground hover:text-destructive focus:outline-none transition-colors"
                          onClick={() => {
                            if (Array.isArray(allowedDomains) && allowedDomains.length > 1) {
                              setAllowedDomains(allowedDomains.filter((_, i) => i !== index));
                            } else {
                              // If removing the last domain, set to * (allow all)
                              setAllowedDomains(['*']);
                            }
                          }}
                          aria-label={`Remove ${typeof domain === 'string' ? domain : 'domain'}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="mt-4">
                <div className="flex">
                  <Input
                    id="domain-input"
                    type="text"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    placeholder="Enter host (e.g., https://example.com)"
                    className="flex-1 rounded-r-none"
                  />
                  <Button
                    onClick={() => {
                      if (newDomain && Array.isArray(allowedDomains)) {
                        // Filter out the wildcard if it exists
                        const filteredDomains = allowedDomains.filter(d => d !== '*');
                        // Add the new domain
                        setAllowedDomains([...filteredDomains, newDomain]);
                        setNewDomain('');
                      }
                    }}
                    className="rounded-l-none"
                  >
                    Add
                  </Button>
                </div>
              </div>
              
                {enableOverrides && allowedDomains.length === 1 && allowedDomains[0] === '*' && (
                  <Alert variant="destructive" className="mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      <AlertTitle className="text-sm font-medium">Warning</AlertTitle>
                    </div>
                    <AlertDescription className="text-xs leading-relaxed">
                      We strongly recommend setting up an allowlist when using overrides.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Enable overrides section (moved up) */}
            <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
              <div className="font-semibold text-card-foreground mb-1">Enable overrides</div>
              <div className="text-muted-foreground text-sm mb-2">Choose which parts of the config can be overridden by the client at the start of the conversation.</div>
              <div className="flex flex-col gap-6 mt-4">
                {/* Agent language toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-card-foreground text-sm font-medium">Agent language</Label>
                    <div className="text-xs text-muted-foreground">Required by Additional Languages</div>
                  </div>
                  <Switch
                    checked={overrideAgentLanguage || (additionalLanguages && additionalLanguages.length > 0)}
                    onCheckedChange={setOverrideAgentLanguage}
                    disabled={additionalLanguages && additionalLanguages.length > 0}
                  />
                </div>
                {/* First message toggle */}
                <div className="flex items-center justify-between">
                  <Label className="text-card-foreground text-sm font-medium">First message</Label>
                  <Switch
                    checked={overrideFirstMessage}
                    onCheckedChange={setOverrideFirstMessage}
                  />
                </div>
                {/* System prompt toggle */}
                <div className="flex items-center justify-between">
                  <Label className="text-card-foreground text-sm font-medium">System prompt</Label>
                  <Switch
                    checked={overrideSystemPrompt}
                    onCheckedChange={setOverrideSystemPrompt}
                  />
                </div>
                {/* Voice toggle */}
                <div className="flex items-center justify-between">
                  <Label className="text-card-foreground text-sm font-medium">Voice</Label>
                  <Switch
                    checked={overrideVoice}
                    onCheckedChange={setOverrideVoice}
                  />
                </div>
              </div>
            </div>

            {/* Call Limits Section (now after overrides) */}
            <div className="bg-card rounded-xl p-6 shadow-sm border border-border mt-6">
              <div className="mb-6">
                <div className="font-semibold text-card-foreground mb-1">Concurrent Calls Limit</div>
                <div className="text-muted-foreground text-sm mb-2">The maximum number of concurrent calls allowed.</div>
                <Input
                  type="number"
                  className="w-32 border-input focus:border-primary focus:ring-2 focus:ring-primary/20"
                  value={agentConcurrencyLimit ?? ''}
                  onChange={e => setAgentConcurrencyLimit(Number(e.target.value))}
                  min="-1"
                />
                <div className="text-xs text-muted-foreground mt-1">Matching the subscription concurrency limit</div>
              </div>
              <div className="mb-6">
                <div className="font-semibold text-card-foreground mb-1">Daily Calls Limit</div>
                <div className="text-muted-foreground text-sm mb-2">The maximum number of calls allowed per day.</div>
                <Input
                  type="number"
                  className="w-32 border-input focus:border-primary focus:ring-2 focus:ring-primary/20"
                  value={dailyCallsLimit ?? ''}
                  onChange={e => setDailyCallsLimit(Number(e.target.value))}
                  min="0"
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="advanced" className="space-y-6 mt-6">
            {/* Advanced tab content placeholder */}
            <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
              <div>
                <div className="font-semibold text-card-foreground mb-1">Turn timeout</div>
                <div className="text-muted-foreground text-sm mb-4">
                  The maximum number of seconds since the user last spoke. If exceeded, the agent will respond and force a turn. A value of -1 means the agent will never timeout and always wait for a response from the user.
                </div>
                <Input
                  type="number"
                  className="w-24 border-input focus:border-primary focus:ring-2 focus:ring-primary/20"
                  value={turnTimeout}
                  onChange={e => setTurnTimeout(Number(e.target.value))}
                />
              </div>
            </div>
            <Card className="border border-border bg-card shadow-none mb-6">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold text-card-foreground mb-1">Silence end call timeout</div>
                  <div className="text-muted-foreground text-sm">The maximum number of seconds since the user last spoke. If exceeded, the call will terminate. A value of -1 means there is no fixed cutoff.</div>
                </div>
                <Input
                  type="number"
                  className="w-32 border-input focus:border-primary focus:ring-2 focus:ring-primary/20 text-right"
                  value={silenceEndCallTimeout}
                  onChange={e => setSilenceEndCallTimeout(Number(e.target.value))}
                />
              </CardContent>
            </Card>
            <Card className="border border-border bg-card shadow-none mb-6">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold text-card-foreground mb-1">Max conversation duration</div>
                  <div className="text-muted-foreground text-sm">The maximum number of seconds that a conversation can last.</div>
                </div>
                <Input
                  type="number"
                  className="w-32 border-input focus:border-primary focus:ring-2 focus:ring-primary/20 text-right"
                  value={maxConversationDuration}
                  onChange={e => setMaxConversationDuration(Number(e.target.value))}
                />
              </CardContent>
            </Card>
          </TabsContent>



          {/* <TabsContent value="widget" className="space-y-6 mt-6">
            <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
              <div>
                <div className="font-semibold text-card-foreground mb-1">Embed code</div>
                <div className="text-muted-foreground text-sm mb-4">
                  Add the following snippet to the pages where you want the conversation widget to be.
                </div>
                <div className="bg-muted p-4 rounded-lg border border-border">
                  <code className="text-sm text-card-foreground">
                    &lt;elevenlabs-convai agent-id="{agentId}"&gt;&lt;/elevenlabs-convai&gt;&lt;script&gt;...&lt;/script&gt;
                  </code>
                </div>
              </div>
            </div>
          </TabsContent> */}
        </Tabs>

        {/* ToastSave component */}
        {hasUnsavedChanges && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
            <ToastSave
              state={toastSaveState}
              onReset={handleClearChanges}
              onSave={handleSave}
              initialText="You have unsaved changes"
              resetText="Clear"
              saveText="Save Changes"
              loadingText="Saving..."
              successText="Changes Saved"
            />
          </div>
        )}

      </div>






      {/* Add URL Dialog */}
    <Dialog open={addUrlDialogOpen} onOpenChange={setAddUrlDialogOpen}>
      <DialogContent className="sm:max-w-[48rem]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-chart-4/10 rounded-lg flex items-center justify-center">
              <Link className="w-4 h-4 text-chart-4" />
            </div>
            Add URL
          </DialogTitle>
          <DialogDescription>
            Import content from a web page by providing a URL and document name.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="documentName">Document Name</Label>
            <Input
              id="documentName"
              type="text"
              placeholder="Enter document name"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              className={formErrors.documentName ? 'border-destructive' : ''}
            />
            {formErrors.documentName && (
              <p className="text-sm text-destructive">{formErrors.documentName}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="documentUrl">URL</Label>
            <Input
              id="documentUrl"
              type="url"
              placeholder="https://website.com"
              value={documentUrl}
              onChange={(e) => setDocumentUrl(e.target.value)}
              className={formErrors.documentUrl ? 'border-destructive' : ''}
            />
            {formErrors.documentUrl && (
              <p className="text-sm text-destructive">{formErrors.documentUrl}</p>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button onClick={handleAddUrl} disabled={isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Add URL'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Add Text Dialog */}
    <Dialog open={addTextDialogOpen} onOpenChange={setAddTextDialogOpen}>
      <DialogContent className="sm:max-w-[48rem]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-chart-2/10 rounded-lg flex items-center justify-center">
              <FileText className="w-4 h-4 text-chart-2" />
            </div>
            Create Text
          </DialogTitle>
          <DialogDescription>
            Add custom text content to your knowledge base by providing a document name and text content.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="textDocumentName">Document Name</Label>
            <Input
              id="textDocumentName"
              type="text"
              placeholder="Enter document name"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              className={formErrors.documentName ? 'border-destructive' : ''}
            />
            {formErrors.documentName && (
              <p className="text-sm text-destructive">{formErrors.documentName}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="documentText">Text Content</Label>
            <Textarea
              id="documentText"
              placeholder="Enter your text content here..."
              value={documentText}
              onChange={(e) => setDocumentText(e.target.value)}
              className={formErrors.documentText ? 'border-destructive' : ''}
              rows={6}
            />
            {formErrors.documentText && (
              <p className="text-sm text-destructive">{formErrors.documentText}</p>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button onClick={handleAddText} disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Text'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Add Files Dialog */}
    <Dialog open={addFilesDialogOpen} onOpenChange={setAddFilesDialogOpen}>
      <DialogContent className="sm:max-w-[32rem]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-chart-1/10 rounded-lg flex items-center justify-center">
              <File className="w-4 h-4 text-chart-1" />
            </div>
            Add Files
          </DialogTitle>
          <DialogDescription>
            Upload documents and files to your knowledge base by selecting files.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>File Upload</Label>
            <Dropzone
              onFileSelect={setSelectedFiles}
              onFileRemove={handleRemoveFile}
              selectedFiles={selectedFiles}
              multiple={true}
              maxSize={21 * 1024 * 1024} // 21MB
            />
            {formErrors.selectedFiles && (
              <p className="text-sm text-destructive">{formErrors.selectedFiles}</p>
            )}
          </div>
          
          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <Label>Selected Files ({selectedFiles.length})</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border">
                    <div className="w-8 h-8 bg-chart-1/10 rounded flex items-center justify-center">
                      <File className="w-4 h-4 text-chart-1" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-card-foreground truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFile(index)}
                      className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <Separator className="my-4" />
        
        <DialogFooter>
          <Button onClick={handleAddFile} disabled={isSubmitting}>
            {isSubmitting ? 'Uploading...' : `Upload ${selectedFiles.length > 0 ? `${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}` : 'Files'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </div>
  );
}

export default AgentDetails; 